import React from 'react';
import { Box, Collapse, Container, Stack, Typography } from '@mui/material';
import { AccessTime, Email, Facebook, Instagram, Phone, WhatsApp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { FooterContactRow, FooterSocialButton, PublicTemplateProps } from './sharedPieces';

// Lean alternative to ClassicTemplate: plain header, the shared search widget, a bare
// fleet listing, and a condensed contact block. No feature grid, no how-it-works, no
// closing CTA banner. Shares no JSX with ClassicTemplate - only the
// data/handlers passed in via props.
export const MinimalTemplate: React.FC<PublicTemplateProps> = ({
  tenantCompany,
  tenantLogoUrl,
  logoLoadError,
  onLogoError,
  tenantLocationSummary,
  tenantWhatsappHref,
  searchFilterCard,
  successAlert,
  renderResultBody,
  datesReady,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* HEADER + SEARCH */}
      <Box sx={{ bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Container maxWidth="md" sx={{ py: { xs: 4, sm: 6 } }}>
          <Stack spacing={1.5} alignItems="center" sx={{ textAlign: 'center', mb: 4 }}>
            {tenantLogoUrl && !logoLoadError && (
              <Box
                component="img"
                src={tenantLogoUrl}
                alt={tenantCompany?.companyName}
                onError={onLogoError}
                sx={{ height: 40, maxWidth: 160, objectFit: 'contain', mb: 1 }}
              />
            )}
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {tenantCompany?.companyName || t('search.hero.fallbackHeadline', 'Find the right car for your trip')}
            </Typography>
            {tenantCompany?.heroTagline && (
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520 }}>
                {tenantCompany.heroTagline}
              </Typography>
            )}
          </Stack>

          {successAlert}
          {searchFilterCard}
        </Container>
      </Box>

      {/* FLEET */}
      <Box sx={{ py: { xs: 4, sm: 6 } }}>
        <Container maxWidth="lg">
          <Collapse in={!datesReady} timeout={400} unmountOnExit>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
              {t('search.fleet.eyebrow', 'Our Fleet')}
            </Typography>
          </Collapse>
          {renderResultBody()}
        </Container>
      </Box>

      {/* CONTACT */}
      {tenantCompany && (
        <Box sx={{ py: { xs: 4, sm: 6 }, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
          <Container maxWidth="sm">
            <Stack spacing={2}>
              {tenantCompany.description && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', mb: 1 }}>
                  {tenantCompany.description}
                </Typography>
              )}
              <Stack spacing={1.5}>
                {tenantCompany.phoneNumber && (
                  <FooterContactRow icon={<Phone fontSize="small" />}>
                    <Typography variant="body2">{tenantCompany.phoneNumber}</Typography>
                  </FooterContactRow>
                )}
                {tenantCompany.email && (
                  <FooterContactRow icon={<Email fontSize="small" />}>
                    <Typography variant="body2">{tenantCompany.email}</Typography>
                  </FooterContactRow>
                )}
                {(tenantLocationSummary || tenantCompany.operatingDays) && (
                  <FooterContactRow icon={<AccessTime fontSize="small" />}>
                    <Stack spacing={0.25}>
                      {tenantLocationSummary && (
                        <Typography variant="body2">{tenantLocationSummary}</Typography>
                      )}
                      {tenantCompany.operatingDays && (
                        <Typography variant="body2" color="text.secondary">
                          {tenantCompany.operatingDays}
                          {tenantCompany.operatingHoursStart && tenantCompany.operatingHoursEnd
                            ? ` · ${tenantCompany.operatingHoursStart}-${tenantCompany.operatingHoursEnd}`
                            : ''}
                        </Typography>
                      )}
                    </Stack>
                  </FooterContactRow>
                )}
              </Stack>
              {(tenantCompany.instagramUrl || tenantCompany.facebookUrl || tenantWhatsappHref) && (
                <Stack direction="row" spacing={1.25}>
                  {tenantWhatsappHref && (
                    <FooterSocialButton href={tenantWhatsappHref} label="WhatsApp" color="#25D366">
                      <WhatsApp fontSize="small" />
                    </FooterSocialButton>
                  )}
                  {tenantCompany.instagramUrl && (
                    <FooterSocialButton href={tenantCompany.instagramUrl} label="Instagram" color="#E1306C">
                      <Instagram fontSize="small" />
                    </FooterSocialButton>
                  )}
                  {tenantCompany.facebookUrl && (
                    <FooterSocialButton href={tenantCompany.facebookUrl} label="Facebook" color="#1877F2">
                      <Facebook fontSize="small" />
                    </FooterSocialButton>
                  )}
                </Stack>
              )}
            </Stack>
          </Container>
        </Box>
      )}
    </>
  );
};
